import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiArrowRight, HiExclamation, HiExclamationCircle, HiInformationCircle } from 'react-icons/hi';

/**
 * El embudo de la operación y lo que la frena.
 *
 * Es la respuesta a «¿qué hago ahora?», y existe porque el Panel no la tenía:
 * se alimentaba solo de pedidos, así que con cero ventas enseñaba cuatro ceros
 * y ya. Cuatro ceros no solo no ayudan — **se leen igual que una pérdida**.
 *
 * ## Por qué el embudo tiene cuatro pasos y no tres
 *
 * «Se publicaron» y «están a la venta» son números distintos. Un anuncio
 * terminado conserva su identificador en la tienda, así que sigue contando como
 * publicado aunque ya no lo vea nadie. Juntarlos diría que tienes escaparate
 * cuando no lo tienes.
 *
 * ## Por qué un aviso en cero no se pinta
 *
 * Una lista de «todo bien» repetida cada día enseña a no mirarla, y el día que
 * aparezca algo de verdad ya nadie la lee. Aquí solo hay avisos cuando hay algo
 * que hacer, y cada uno lleva a donde se hace.
 */

/**
 * La gravedad decide el color, y el color nunca va solo: cada aviso lleva su
 * icono y su frase. Quien no distinga el rojo del ámbar lee exactamente lo
 * mismo que los demás.
 */
const GRAVEDAD = {
    // Cuesta dinero o clientes ahora mismo.
    pedidos_sin_seguimiento: 'grave',
    pedidos_sin_proveedor: 'grave',
    // Frena la operación, pero nadie está esperando.
    anuncios_terminados: 'aviso',
    sin_tienda: 'aviso',
    sin_precio: 'aviso',
    sin_flete: 'aviso',
    // Conviene mirarlo.
    sin_comprobar: 'leve'
};

const ESTILO = {
    grave: {
        icono: HiExclamationCircle,
        caja: 'border-danger-500/40 bg-danger-500/5',
        texto: 'text-danger-500'
    },
    aviso: {
        icono: HiExclamation,
        caja: 'border-warning-500/40 bg-warning-500/5',
        texto: 'text-warning-600 dark:text-warning-500'
    },
    leve: {
        icono: HiInformationCircle,
        caja: 'border-slate-200 dark:border-slate-700',
        texto: 'text-slate-500 dark:text-slate-400'
    }
};

const ORDEN = ['grave', 'aviso', 'leve'];

function Paso({ valor, etiqueta, enlace, apagado }) {
    const contenido = (
        <>
            <p className={`text-3xl font-bold ${apagado ? 'text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-white'}`}>
                {valor}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{etiqueta}</p>
        </>
    );

    return enlace ? (
        <Link to={enlace} className="block w-full text-center rounded-xl p-4 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors">
            {contenido}
        </Link>
    ) : (
        <div className="w-full text-center p-4">{contenido}</div>
    );
}

export default function EstadoDeLaOperacion({ estado }) {
    const { t } = useTranslation();

    if (!estado) return null;

    const { embudo, avisos = [] } = estado;

    const pasos = [
        { clave: 'importados', valor: embudo.importados, enlace: '/products/imported' },
        { clave: 'publicados', valor: embudo.publicados, enlace: '/products/imported?filtro=published' },
        { clave: 'a_la_venta', valor: embudo.aLaVenta, enlace: '/products/imported?filtro=published' },
        { clave: 'vendidos', valor: embudo.vendidos, enlace: '/orders' }
    ];

    const ordenados = [...avisos].sort(
        (a, b) => ORDEN.indexOf(GRAVEDAD[a.clave] || 'leve') - ORDEN.indexOf(GRAVEDAD[b.clave] || 'leve')
    );

    return (
        <div className="space-y-6">
            <div className="card">
                <h2 className="section-title mb-4">{t('dashboard.funnel_title')}</h2>

                {/* En un teléfono los cuatro pasos en fila NO caben: medido,
                    470 px de contenido en 375, con «Vendidos» fuera de pantalla
                    y el cuerpo desplazándose de lado. Ahí se apilan en dos por
                    dos y las flechas desaparecen — sin fila no hay dirección
                    que señalar, y el orden de lectura ya la da. */}
                <div className="grid grid-cols-2 gap-1 sm:flex sm:items-center sm:gap-0">
                    {pasos.map((paso, i) => (
                        <div key={paso.clave} className="sm:flex-1 sm:flex sm:items-center">
                            <Paso
                                valor={paso.valor}
                                etiqueta={t(`dashboard.funnel_${paso.clave}`)}
                                enlace={paso.valor > 0 ? paso.enlace : null}
                                apagado={paso.valor === 0}
                            />
                            {i < pasos.length - 1 && (
                                <HiArrowRight
                                    className="hidden sm:block w-4 h-4 text-slate-300 dark:text-slate-600 shrink-0"
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* La diferencia entre publicados y a la venta es la que se
                    escapa mirando solo el número grande. */}
                {embudo.publicados > 0 && embudo.aLaVenta === 0 && (
                    <p className="text-sm text-warning-600 dark:text-warning-500 mt-3 text-center">
                        {t('dashboard.funnel_nothing_live')}
                    </p>
                )}
            </div>

            {ordenados.length > 0 && (
                <div className="card">
                    <h2 className="section-title mb-4">{t('dashboard.blockers_title')}</h2>

                    <ul className="space-y-2">
                        {ordenados.map((aviso) => {
                            const estilo = ESTILO[GRAVEDAD[aviso.clave] || 'leve'];
                            const Icono = estilo.icono;

                            return (
                                <li key={aviso.clave}>
                                    <Link
                                        to={aviso.enlace}
                                        className={`flex items-center gap-3 p-3 rounded-lg border ${estilo.caja} hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors`}
                                    >
                                        <Icono className={`w-5 h-5 shrink-0 ${estilo.texto}`} aria-hidden="true" />
                                        <span className="text-sm text-slate-700 dark:text-slate-200 flex-1">
                                            {t(`dashboard.aviso_${aviso.clave}`, { count: aviso.cuenta })}
                                        </span>
                                        <HiArrowRight className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
